import { HealthSourceNote } from "@/components/health/HealthSourceInfo";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Search, Flame, Users, UtensilsCrossed, Pencil, Trash2, Filter, X, ChevronLeft, ChevronRight, Heart } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MealPlanTemplateForm from "@/components/meals/MealPlanTemplateForm";
import { useAuth } from "@/lib/AuthContext";
import { useTimeFormatter } from "@/lib/timeFormat";

function to12Hour(time) {
  if (!time) return "";
  const [h, m] = time.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, "0")} ${period}`;
}

const goalColors = {
  bulking: "bg-primary/10 text-primary",
  cutting: "bg-destructive/10 text-destructive",
  maintenance: "bg-emerald-500/10 text-emerald-500",
  clean_eating: "bg-amber-500/10 text-amber-500",
};

const goalLabels = {
  bulking: "Bulking",
  cutting: "Cutting",
  maintenance: "Maintenance",
  clean_eating: "Clean Eating",
};

export default function MealPlanTemplateLibrary() {
  const fmtTime = useTimeFormatter();
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState("all");
  const [calorieRange, setCalorieRange] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const itemsPerPage = 6;
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ["mealPlanTemplates"],
    queryFn: () => base44.entities.MealPlanTemplate.list("-created_date"),
  });

  const { data: communityMealPlans = [], isLoading: plansLoading } = useQuery({
    queryKey: ["communityMealPlans"],
    queryFn: () => base44.entities.MealPlan.filter({ visible_on_community: true }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.MealPlanTemplate.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["mealPlanTemplates"] }),
  });

  const { data: savedPlans = [] } = useQuery({
    queryKey: ["savedMealPlans"],
    queryFn: () => base44.entities.SavedPlan.filter({ user_id: user?.id, plan_type: "meal" }),
    enabled: !!user,
  });

  const savePlanMutation = useMutation({
    mutationFn: async ({ planId, isSaved }) => {
      if (isSaved) {
        const existing = savedPlans.find(s => s.plan_id === planId);
        if (existing) await base44.entities.SavedPlan.delete(existing.id);
      } else {
        await base44.entities.SavedPlan.create({ user_id: user.id, plan_id: planId, plan_type: "meal" });
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["savedMealPlans"] }),
  });

  const isSaved = (planId) => savedPlans.some(s => s.plan_id === planId);

  const isLoading = templatesLoading || plansLoading;

  const allPlans = [
    ...templates.map(t => ({ ...t, type: 'template' })),
    ...communityMealPlans.map(p => ({ ...p, type: 'meal' }))
  ];

  const filtered = allPlans.filter((p) => {
    const q = search.toLowerCase();
    const matchesSearch = (
      p.name?.toLowerCase().includes(q) ||
      p.description?.toLowerCase().includes(q) ||
      p.goal?.toLowerCase().includes(q) ||
      (p.tags || []).some((tag) => tag.toLowerCase().includes(q))
    );

    const matchesGoal = selectedGoal === "all" || p.goal === selectedGoal;

    let matchesCalories = true;
    if (calorieRange !== "all" && p.daily_calories) {
      const cal = p.daily_calories;
      if (calorieRange === "low" && cal > 1800) matchesCalories = false;
      if (calorieRange === "medium" && (cal < 1800 || cal > 2500)) matchesCalories = false;
      if (calorieRange === "high" && cal < 2500) matchesCalories = false;
    }

    return matchesSearch && matchesGoal && matchesCalories;
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedPlans = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleResetFilters = () => {
    setSearch("");
    setSelectedGoal("all");
    setCalorieRange("all");
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      <div>
        <HealthSourceNote topic="planning" />
        <h2 className="text-2xl font-display font-bold flex items-center gap-2">
          <Users className="w-6 h-6 text-primary" /> Community Plans
        </h2>
        <p className="text-muted-foreground text-sm mt-1">
          Discover meal plans shared by the community
        </p>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search templates..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              className="pl-9"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="gap-2"
          >
            <Filter className="w-4 h-4" />
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 bg-secondary/30 rounded-lg">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Goal</label>
              <Select value={selectedGoal} onValueChange={(v) => { setSelectedGoal(v); setCurrentPage(1); }}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Goals</SelectItem>
                  <SelectItem value="bulking">Bulking</SelectItem>
                  <SelectItem value="cutting">Cutting</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="clean_eating">Clean Eating</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground">Daily Calories</label>
              <Select value={calorieRange} onValueChange={(v) => { setCalorieRange(v); setCurrentPage(1); }}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ranges</SelectItem>
                  <SelectItem value="low">&lt; 1800 kcal</SelectItem>
                  <SelectItem value="medium">1800 - 2500 kcal</SelectItem>
                  <SelectItem value="high">&gt; 2500 kcal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleResetFilters}
              className="justify-start gap-2 text-xs"
            >
              <X className="w-3 h-3" />
              Reset Filters
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="grid md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-40 rounded-xl bg-muted/30 animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center border-0 shadow-sm">
          <UtensilsCrossed className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <p className="text-muted-foreground">
            {search || selectedGoal !== "all" || calorieRange !== "all" ? "No plans match your filters." : "No community plans yet."}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {paginatedPlans.map((plan) => {
              const planSaveCount = savedPlans.filter(s => s.plan_id === plan.id).length;
              const isOwner = plan.created_by_id === user?.id;
              const isAdmin = user?.role === "admin";
              const canEdit = isOwner || isAdmin;
              return (
              <Card key={plan.id} className="border-0 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="font-display text-base">{plan.name}</CardTitle>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {plan.goal && (
                          <Badge className={`text-xs ${goalColors[plan.goal]}`}>
                            {goalLabels[plan.goal]}
                          </Badge>
                        )}
                        {plan.daily_calories && (
                          <Badge variant="outline" className="text-xs gap-1">
                            <Flame className="w-3 h-3" /> {plan.daily_calories} kcal
                          </Badge>
                        )}
                        {plan.type === 'template' && (plan.tags || []).map((tag) => (
                          <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                        ))}
                        <Badge variant="outline" className="text-xs">
                          {plan.type === 'template' ? 'Template' : 'Community'}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0 ml-2">
                      {canEdit && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { setEditing(plan); setShowForm(true); }}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => deleteMutation.mutate(plan.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {plan.type === 'meal' && user && !canEdit && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => savePlanMutation.mutate({ planId: plan.id, isSaved: isSaved(plan.id) })}
                        >
                          <Heart className={`w-4 h-4 ${isSaved(plan.id) ? "fill-destructive text-destructive" : "text-muted-foreground"}`} />
                        </Button>
                      )}
                    </div>
                  </div>
                  {plan.description && (
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{plan.description}</p>
                  )}
                  {(plan.daily_protein_g || plan.daily_carbs_g || plan.daily_fat_g) && (
                    <div className="flex gap-4 mt-2 text-xs">
                      {plan.daily_protein_g && <span className="text-primary font-semibold">P: {plan.daily_protein_g}g</span>}
                      {plan.daily_carbs_g && <span className="text-amber-500 font-semibold">C: {plan.daily_carbs_g}g</span>}
                      {plan.daily_fat_g && <span className="text-accent font-semibold">F: {plan.daily_fat_g}g</span>}
                    </div>
                  )}
                </CardHeader>
                <CardContent className="pt-0">
                  {(plan.meals || []).length > 0 && (
                    <div className="space-y-1">
                      {plan.meals.map((meal, mi) => (
                        <div key={mi} className="flex items-center justify-between text-xs text-muted-foreground">
                          <span className="font-medium">{meal.meal_name}</span>
                          <span>{(meal.foods || []).length} foods{meal.time ? ` · ${fmtTime(meal.time)}` : ""}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {plan.type === 'meal' && (
                    <div className="flex items-center gap-1 mt-2 pt-2 border-t border-muted text-xs text-muted-foreground">
                      <Users className="w-3 h-3" />
                      <span>{planSaveCount} saved</span>
                    </div>
                  )}
                </CardContent>
              </Card>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 bg-secondary/20 rounded-lg">
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages} ({filtered.length} total plans)
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <MealPlanTemplateForm
        open={showForm}
        onOpenChange={setShowForm}
        initialData={editing}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["mealPlanTemplates"] });
          setShowForm(false);
          setEditing(null);
        }}
      />
    </div>
  );
}